/**
 * The distillation table: one row per subject an LLM judged, per query.
 *
 * A search action's other stages — the SQL filter it planned, the SharePoint
 * permission filter, the PC-visibility gate — are separate ACTIONS rows now, and
 * each records its own counts and the records it dropped. What is left for this
 * table is the distillation itself: what each judging call was shown, what it
 * decided, and why. The rows are read straight off the LLM_REQUESTS rows the
 * call wrote (see mcpDistillation), so nothing here depends on a per-record copy
 * being kept on the action row.
 *
 * A whole deal set can fan out into hundreds of judgements, which is why the
 * filter and the per-query chips are on the panel rather than left to the
 * browser's find.
 */
import { useState, type JSX } from "react";
import QueryFilterChips from "./QueryFilterChips";
import {
  countRecords,
  filterDistillationGroups,
  verdictLabel,
  type DistillationGroup,
  type DistillationRecord,
} from "./mcpDistillation";

/** What a group with no query is called — see groupMcpLlmRequestsByQuery. */
const UNATTRIBUTED = "Not attributed to a query";

function Verdict({ record }: { record: DistillationRecord }): JSX.Element {
  if (record.error) {
    // Kept, but never judged: the servers fail open, so this is not a "yes".
    return (
      <span className="px-2 py-0.5 rounded-sm text-xs font-medium bg-amber-100 text-amber-800">
        {verdictLabel(record)}
      </span>
    );
  }
  if (record.verdict === null) return <span className="text-gray-400">—</span>;
  const tone = record.verdict ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600";
  return (
    <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${tone}`}>
      {verdictLabel(record)}
    </span>
  );
}

function Row({
  record,
  requestHref,
}: {
  record: DistillationRecord;
  requestHref?: (llmRequestId: string) => string;
}): JSX.Element {
  return (
    <tr className="border-t border-gray-100 align-top">
      <td className="px-3 py-2 font-mono text-xs text-gray-500">{record.identifier}</td>
      <td className="px-3 py-2 text-gray-900">
        {record.title}
        {record.subtitle && <span className="block text-xs text-gray-500">{record.subtitle}</span>}
      </td>
      <td className="px-3 py-2 text-gray-700">
        {record.detail ?? <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-2">
        <Verdict record={record} />
      </td>
      <td className="px-3 py-2 text-gray-700">
        {record.error ?? record.reason ?? <span className="text-gray-400">No reason recorded</span>}
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {/* The judging call itself: the prompt behind the verdict, in full. */}
        {requestHref ? (
          <a href={requestHref(record.llmRequestId)} className="text-blue-600 hover:underline">
            {record.llmRequestId}
          </a>
        ) : (
          <span className="text-gray-500">{record.llmRequestId}</span>
        )}
      </td>
    </tr>
  );
}

function Group({
  group,
  requestHref,
}: {
  group: DistillationGroup;
  requestHref?: (llmRequestId: string) => string;
}): JSX.Element {
  const kind = group.records[0].kind === "deal" ? "deal(s)" : "chunk(s)";
  return (
    <div>
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Query {group.position + 1}
        </p>
        <p className="text-sm text-gray-900">{group.query ?? UNATTRIBUTED}</p>
        <p className="text-xs text-gray-500">
          {group.records.length} {kind} judged
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">ID</th>
              <th className="px-3 py-2 text-left font-medium">Subject</th>
              <th className="px-3 py-2 text-left font-medium">Detail</th>
              <th className="px-3 py-2 text-left font-medium">Verdict</th>
              <th className="px-3 py-2 text-left font-medium">Reason</th>
              <th className="px-3 py-2 text-left font-medium">LLM request</th>
            </tr>
          </thead>
          <tbody>
            {group.records.map((record) => (
              <Row key={record.llmRequestId} record={record} requestHref={requestHref} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Renders nothing when the action judged nothing.
 *
 * Core-agent actions, the gate stages, and MCP rows written before the servers
 * stamped their prompts all produce no records — an empty table there would read
 * as "this search considered nothing", so there is no table at all.
 */
export default function DistillationTable({
  groups,
  requestHref,
}: {
  groups: DistillationGroup[];
  requestHref?: (llmRequestId: string) => string;
}): JSX.Element | null {
  const [selected, setSelected] = useState<number | null>(null);
  const [term, setTerm] = useState("");

  if (groups.length === 0) return null;
  const total = countRecords(groups);
  // A hidden control exerts no force. The chips only appear above one group, so
  // a selection left over from a wider fan-out would otherwise keep filtering
  // with nothing on screen to clear it — an empty table and no way out.
  const offersChips = groups.length > 1;
  const activeQuery = offersChips ? selected : null;
  const visible = filterDistillationGroups(groups, term, activeQuery);

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="px-4 py-3 border-b border-gray-200 space-y-2">
        <h3 className="text-base font-medium text-gray-900">Distillation ({total})</h3>
        <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Filter judgements (fuzzy)"
          aria-label="Filter judgements"
          className="w-full sm:w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md
            focus:outline-hidden focus:ring-2 focus:ring-blue-500"
        />
        {offersChips && (
          <QueryFilterChips
            chips={groups.map((group) => ({
              position: group.position,
              label: group.query ?? UNATTRIBUTED,
              count: group.records.length,
            }))}
            total={total}
            selected={activeQuery}
            onSelect={setSelected}
            ariaLabel="Filter judgements by query"
          />
        )}
      </div>
      {visible.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-500">No judgements match this filter.</div>
      ) : (
        visible.map((group) => (
          <Group key={group.position} group={group} requestHref={requestHref} />
        ))
      )}
    </div>
  );
}
